import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useDispatch } from 'react-redux'
import useInfiniteData from '../../../../hooks/useInfiniteData'
import { getLedByCommunityList, AdminSplitFeesListRequestArgs } from '../api'
import { useDebounce } from '../../../../hooks/hooks'
import { setLoading } from '../../../../redux/store/common/slice'
import { LedByCommunityData } from './types'

type ContextProps = {
  state: {
    list: LedByCommunityData[]
    hasNextPage: boolean
    search: string
  }
  actions: {
    handleLoadMore: VoidFunction
    setSearch: Dispatch<SetStateAction<string>>
    getTableData: (args?: AdminSplitFeesListRequestArgs) => Promise<void>
  }
}

const LedByCommunityContext = createContext<ContextProps>({
  state: {} as ContextProps['state'],
  actions: {} as ContextProps['actions']
})

export const LedByCommunityContextProvider: FC<PropsWithChildren> = ({
  children
}) => {
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [list, setList] = useState<LedByCommunityData[]>([])
  const dispatch = useDispatch()
  const debouncedSearch = useDebounce(search, 500)

  useEffect(() => {
    getTableData({
      search: debouncedSearch || undefined
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  const getTableData = useCallback(
    async (args?: AdminSplitFeesListRequestArgs) => {
      try {
        dispatch(setLoading(true))
        const { data } = await getLedByCommunityList(args)
        setList(data.results)
        setTotal(data.count)
      } finally {
        dispatch(setLoading(false))
      }
    },
    [dispatch]
  )

  const loadMore = async (args: AdminSplitFeesListRequestArgs) => {
    try {
      const { data } = await getLedByCommunityList(args)
      if (args?.offset && Number(args.offset) !== 0) {
        setList((prev) => [...prev, ...data.results])
      }
    } finally {
    }
  }

  const { hasNextPage, handleLoadMore } = useInfiniteData({
    total,
    loadMore
  })

  const context = useMemo(
    () => ({
      state: {
        list,
        hasNextPage,
        search
      },
      actions: {
        handleLoadMore,
        setSearch,
        getTableData
      }
    }),
    [list, hasNextPage, search, handleLoadMore, getTableData]
  )

  return (
    <LedByCommunityContext.Provider value={context}>
      {children}
    </LedByCommunityContext.Provider>
  )
}

export const useLedByCommunityListContext = () =>
  useContext(LedByCommunityContext)

export default LedByCommunityContextProvider
